import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import './BackendWorkflowPanel.css';
import { API_BASE } from '../apiBase';

export interface WorkflowEventSnapshot {
  flow: string;
  stage: string;
  title: string;
  detail: string;
  status: 'idle' | 'running' | 'completed' | 'warning' | 'error';
  progress?: number | null;
  timestamp?: string;
  summary?: string;
  extra?: Record<string, unknown>;
}

function summaryPointsFromExtra(extra?: Record<string, unknown>): string[] {
  const raw = extra?.summary_points;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item) => String(item || '').trim()).filter(Boolean).slice(0, 6);
}

interface BackendWorkflowPanelProps {
  mobile: boolean;
  fallbackTip?: string;
  socket?: any;
  onClose?: () => void;
  docked?: boolean;
}

const WORKFLOW_STATUS_URL = `${API_BASE}/api/workflow-status`;

const FLOW_LABELS: Record<string, string> = {
  idle: '空闲',
  structure_generation: '结构生成',
  script_generation: '剧本生成',
  collaboration: '协作同步',
};

const STATUS_LABELS: Record<WorkflowEventSnapshot['status'], string> = {
  idle: '待命',
  running: '进行中',
  completed: '已完成',
  warning: '需关注',
  error: '异常',
};

function normalizeWorkflowEvent(payload: any): WorkflowEventSnapshot | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }

  const allowedStatus = new Set(['idle', 'running', 'completed', 'warning', 'error']);
  const progress =
    typeof payload.progress === 'number' && Number.isFinite(payload.progress)
      ? Math.max(0, Math.min(100, Math.round(payload.progress)))
      : null;

  return {
    flow: String(payload.flow || 'idle'),
    stage: String(payload.stage || 'idle'),
    title: String(payload.title || '后端待命'),
    detail: String(payload.detail || ''),
    status: allowedStatus.has(String(payload.status))
      ? (payload.status as WorkflowEventSnapshot['status'])
      : 'idle',
    progress,
    timestamp: typeof payload.timestamp === 'string' ? payload.timestamp : '',
    summary: typeof payload.summary === 'string' ? payload.summary : '',
    extra:
      payload.extra && typeof payload.extra === 'object' && !Array.isArray(payload.extra)
        ? payload.extra
        : undefined,
  };
}

function normalizeWorkflowHistory(items: any): WorkflowEventSnapshot[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => normalizeWorkflowEvent(item))
    .filter((item): item is WorkflowEventSnapshot => Boolean(item));
}

function formatTime(timestamp?: string): string {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function clampProgress(value?: number | null): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}

const BackendWorkflowPanel: React.FC<BackendWorkflowPanelProps> = ({
  mobile,
  fallbackTip,
  socket: externalSocket,
  onClose,
  docked = false,
}) => {
  const [connected, setConnected] = useState<boolean>(false);
  const [current, setCurrent] = useState<WorkflowEventSnapshot | null>(null);
  const [history, setHistory] = useState<WorkflowEventSnapshot[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(mobile);

  useEffect(() => {
    setCollapsed(mobile);
  }, [mobile]);

  useEffect(() => {
    let active = true;
    const enableWebsocket = String(process.env.REACT_APP_ENABLE_SOCKET_WEBSOCKET || '').trim() === '1';
    const client = externalSocket || (enableWebsocket ? io(API_BASE, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 10000,
      reconnectionDelayMax: 20000,
    }) : null);
    const ownsSocket = Boolean(client && !externalSocket);

    const loadWorkflowStatus = async () => {
      try {
        const response = await axios.get(WORKFLOW_STATUS_URL);
        if (!active) {
          return;
        }
        setCurrent(normalizeWorkflowEvent(response.data?.current));
        setHistory(normalizeWorkflowHistory(response.data?.history));
      } catch (error) {
        if (active) {
          console.warn('Failed to load workflow status', error);
        }
      }
    };

    const handleWorkflowStatus = (payload: any) => {
      if (!active) {
        return;
      }
      setCurrent(normalizeWorkflowEvent(payload?.current));
      setHistory(normalizeWorkflowHistory(payload?.history));
    };

    setConnected(Boolean(client?.connected));
    loadWorkflowStatus();

    const handleConnect = () => {
      setConnected(true);
      loadWorkflowStatus();
    };

    const handleDisconnect = () => {
      setConnected(false);
    };

    client?.on('connect', handleConnect);
    client?.on('disconnect', handleDisconnect);
    client?.on('workflow_status', handleWorkflowStatus);

    return () => {
      active = false;
      client?.off('connect', handleConnect);
      client?.off('disconnect', handleDisconnect);
      client?.off('workflow_status', handleWorkflowStatus);
      if (ownsSocket) {
        client?.close();
      }
    };
  }, [externalSocket]);

  const safeCurrent = current || {
    flow: 'idle',
    stage: 'idle',
    title: '后端待命',
    detail: '',
    status: 'idle' as const,
    progress: 0,
    timestamp: '',
    summary: '',
  };

  const flowLabel = FLOW_LABELS[safeCurrent.flow] || '后端流程';
  const statusLabel = STATUS_LABELS[safeCurrent.status] || '进行中';
  const progressValue = clampProgress(safeCurrent.progress);
  const summaryText = safeCurrent.summary || safeCurrent.detail || fallbackTip || '等待新的生成请求。';
  const historyItems = useMemo(() => history.slice(-6).reverse(), [history]);
  const currentSummaryPoints = summaryPointsFromExtra(safeCurrent.extra);

  return (
    <section
      className={[
        'backend-workflow',
        `is-${safeCurrent.status}`,
        mobile ? 'is-mobile' : '',
        collapsed ? 'is-collapsed' : '',
        docked ? 'is-docked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="backend-workflow-panel"
    >
      <div className="backend-workflow__header">
        <div>
          <div className="backend-workflow__eyebrow">
            <span className={`backend-workflow__signal ${connected ? 'is-online' : 'is-offline'}`} />
            {connected ? '后端在线' : '后端离线'}
          </div>
          <h3>后端动态</h3>
          <p>
            {flowLabel} · {statusLabel}
          </p>
        </div>
        <div className="backend-workflow__actions">
          <button
            type="button"
            className="backend-workflow__toggle"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? '展开后端动态面板' : '收起后端动态面板'}
          >
            {collapsed ? '展开' : '收起'}
          </button>
          {onClose && (
            <button
              type="button"
              className="backend-workflow__toggle backend-workflow__toggle--ghost"
              onClick={onClose}
              aria-label="隐藏后端动态面板"
            >
              隐藏
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="backend-workflow__hero">
            <div className="backend-workflow__chips">
              <span className="backend-workflow__chip">{flowLabel}</span>
              <span className={`backend-workflow__chip is-${safeCurrent.status}`}>{statusLabel}</span>
              {safeCurrent.timestamp && (
                <span className="backend-workflow__chip is-muted">{formatTime(safeCurrent.timestamp)}</span>
              )}
            </div>
            <strong>{safeCurrent.title || '后端待命'}</strong>
            <p>{safeCurrent.detail || summaryText}</p>
          </div>

          <div className="backend-workflow__progress">
            <div className="backend-workflow__progress-bar">
              <span style={{ width: `${progressValue}%` }} />
            </div>
            <label>{progressValue}%</label>
          </div>

          <div className="backend-workflow__summary">
            <span>系统摘要</span>
            <p>{summaryText}</p>
            {currentSummaryPoints.length > 0 && (
              <div className="backend-workflow__summary-points">
                {currentSummaryPoints.map((item) => (
                  <span key={item} className="backend-workflow__summary-pill">
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="backend-workflow__history">
            <div className="backend-workflow__history-title">
              <span>最近步骤</span>
              <small>{historyItems.length} 条</small>
            </div>
            {historyItems.length === 0 ? (
              <div className="backend-workflow__empty">还没有新的流程记录。</div>
            ) : (
              <div className="backend-workflow__history-list">
                {historyItems.map((item, index) => {
                  const historySummaryPoints = summaryPointsFromExtra(item.extra);
                  return (
                  <article
                    key={`${item.timestamp || 'no-time'}-${item.stage}-${index}`}
                    className={`backend-workflow__history-item is-${item.status}`}
                  >
                    <div className="backend-workflow__history-dot" />
                    <div>
                      <div className="backend-workflow__history-row">
                        <strong>{item.title || item.stage}</strong>
                        <span>{formatTime(item.timestamp)}</span>
                      </div>
                      <p>{item.detail || item.summary || '流程状态已更新。'}</p>
                      {historySummaryPoints.length > 0 && (
                        <div className="backend-workflow__summary-points is-compact">
                          {historySummaryPoints.map((point) => (
                            <span key={point} className="backend-workflow__summary-pill">
                              {point}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default BackendWorkflowPanel;
